"use client"
import IconArrowLeft from "@/app/(site)/_components/Atoms/Icons/IconArrowLeft"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import CTAGroup from "@/app/(site)/_components/GCSanityModules/ShowcaseModule/CTAGroup"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { HIT_TARGET_CLASS } from "@/src/utils/a11y/hitTarget"
import { clsx } from "@/src/utils/clsx"
import { useLangCode } from "@/src/utils/locale/useLangCode"
import { t } from "@/translations"
import { type CTAGroupType } from "@/types/gc-modules-types"
import { useRef } from "react"

type Props = {
    ctas: CTAGroupType[]
    isAboveTheFold?: boolean
}

export default function EditorialCtaCarousel({ ctas, isAboveTheFold = false }: Props) {
    const langCode = useLangCode()
    const carouselRef = useRef<HTMLDivElement>(null)
    const isSingle = ctas.length === 1
    const isMultiple = ctas.length >= 2
    // Arrows are only meaningful when the row can actually overflow. Breakpoints
    // for when 2 vs 3+ cards stop overflowing differ, so each case gets its own
    // upper-bound hide rule. Handled in CSS — no JS measurement needed.
    const isTwoCtas = ctas.length === 2
    const hasThreeOrMoreCtas = ctas.length > 2

    const scrollCarousel = (direction: "prev" | "next") => {
        if (!carouselRef.current) return
        const firstCard = carouselRef.current.firstElementChild as HTMLElement | null
        const cardWidth = firstCard ? firstCard.offsetWidth + 16 : 300
        carouselRef.current.scrollBy({
            left: direction === "next" ? cardWidth : -cardWidth,
            behavior: "smooth"
        })
    }

    return (
        <div
            className={clsx(
                "flex min-w-0 flex-col",
                "lg:gap-16 lg:pt-9",
                isMultiple && "lg:ml-[-12px]"
            )}
        >
            <div
                ref={carouselRef}
                className={clsx(
                    "flex items-start overflow-x-auto scrollbar-hide",
                    "-mx-12 gap-12 px-12 sm:-mx-16 sm:px-16",
                    "lg:mx-0 lg:gap-16 lg:px-0 lg:pr-24",
                    isSingle && "lg:justify-end"
                )}
            >
                {ctas.map((card, index) => (
                    <CTAGroup
                        key={`${index}-${card._key}`}
                        CTAGroup={card}
                        imageSizes="(min-width: 1024px) 20vw, 50vw"
                        isAboveTheFold={isAboveTheFold}
                        className="w-[272px] max-w-[272px] shrink-0 gap-6 lg:w-[340px] lg:max-w-[340px]"
                        imageAspect="aspect-[1/1]"
                        headingClassName="Trade_Gothic_Text_14_150_0_700 lg:Trade_Gothic_Text_16_150_0_700"
                        enableHover
                    />
                ))}
            </div>
            {isMultiple && (
                <div
                    className={clsx(
                        "hidden h-26 items-center justify-end gap-8 pr-24 lg:flex",
                        isTwoCtas && "min-[1420px]:hidden",
                        hasThreeOrMoreCtas && "min-[2130px]:hidden"
                    )}
                >
                    <button
                        type="button"
                        onClick={() => scrollCarousel("prev")}
                        className={clsx("p-5", FOCUS_VISIBLE_CLASS, HIT_TARGET_CLASS)}
                        aria-label={t(langCode, "common.previous_item")}
                    >
                        <IconArrowLeft aria-hidden="true" className="h-16 w-16" />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollCarousel("next")}
                        className={clsx("p-5", FOCUS_VISIBLE_CLASS, HIT_TARGET_CLASS)}
                        aria-label={t(langCode, "common.next_item")}
                    >
                        <IconArrowRight aria-hidden="true" className="h-16 w-16" />
                    </button>
                </div>
            )}
        </div>
    )
}
